import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { TierBadgeLarge } from "./components/TierBadge";
import { hasTierAdvanced } from "shared/config/adaptiveLearningConfig";
import AdaptiveLearningApi from "shared/services/AdaptiveLearningApi";
import Confetti from "react-confetti";

/**
 * Results Page
 * Displays session results, score, feedback, and tier advancement
 */
const ResultsPage = ({ 
  sessionId: propSessionId,
  sessionData: propSessionData,
  previousTier: propPreviousTier,
  onContinueLearning, 
  onGoToDashboard 
}) => {
  const { sessionId: paramSessionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Use props if provided (StudentHub mode), otherwise use params/state (standalone mode)
  const sessionId = propSessionId || paramSessionId;
  
  const [session, setSession] = useState(
    propSessionData || location.state?.session || null
  );
  const [previousTier, setPreviousTier] = useState(
    propPreviousTier || location.state?.previousTier || null
  );
  const [loading, setLoading] = useState(!session);
  const [error, setError] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);

  // Fetch session if not provided in state or props
  useEffect(() => {
    if (!session && sessionId) {
      AdaptiveLearningApi.getSession(sessionId)
        .then((data) => {
          setSession(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching session:", err);
          setError(err.message);
          setLoading(false);
        });
    }
  }, [session, sessionId]);

  // Check for tier advancement and show celebration
  useEffect(() => {
    if (session && previousTier) {
      const advanced = hasTierAdvanced(previousTier, session.progressTier);
      if (advanced) {
        setShowCelebration(true);
        setTimeout(() => setShowCelebration(false), 5000);
      }
    }
  }, [session, previousTier]);

  // Parse arrays from JSON strings
  const parseJsonArray = (jsonString) => {
    try {
      if (!jsonString) return [];
      if (Array.isArray(jsonString)) return jsonString;
      return JSON.parse(jsonString);
    } catch {
      return [];
    }
  };

  const strengthAreas = parseJsonArray(session?.strengthAreas);
  const improvementAreas = parseJsonArray(session?.improvementAreas);

  const handleContinueLearning = () => {
    // If onContinueLearning prop provided (StudentHub mode), use it
    if (onContinueLearning) {
      onContinueLearning();
    } else {
      // Otherwise use React Router navigation (standalone mode)
      navigate(`/adaptive-learning/session/${session.conceptCode}`);
    }
  };

  const handleGoToDashboard = () => {
    // If onGoToDashboard prop provided (StudentHub mode), use it
    if (onGoToDashboard) {
      onGoToDashboard();
    } else {
      // Otherwise use React Router navigation (standalone mode)
      navigate("/adaptive-learning");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 max-w-md text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Session Not Found
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error || "Unable to load session results."}
          </p>
          <button
            onClick={handleGoToDashboard}
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors"
          >
            {onGoToDashboard ? "Back to Dashboard" : "Go to Dashboard"}
          </button>
        </div>
      </div>
    );
  }

  // API returns 'score' field (percentage 0-100), not 'scorePercentage'
  const scorePercentage = session.score || 0;
  const tierAdvanced = previousTier ? hasTierAdvanced(previousTier, session.progressTier) : false;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-4 md:p-8">
      {/* Confetti Animation */}
      {showCelebration && <Confetti numberOfPieces={200} recycle={false} />}

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-extrabold text-gray-900 dark:text-white mb-2">
            🎉 Results
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            {session.conceptName || session.conceptCode}
          </p>
        </div>

        {/* Score Card */}
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 mb-6">
          <div className="text-center mb-8">
            {/* Score Display */}
            <div className="inline-flex items-center justify-center w-40 h-40 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg mb-4">
              <div>
                <div className="text-6xl font-extrabold">
                  {session.correctAnswers || 0}
                </div>
                <div className="text-2xl font-medium">
                  / {session.totalQuestions || 0}
                </div>
              </div>
            </div>

            {/* Score Percentage */}
            <div className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
              {scorePercentage.toFixed(0)}%
            </div>

            {/* Performance Message */}
            <div className="text-lg text-gray-600 dark:text-gray-400 mb-6">
              {getPerformanceMessage(scorePercentage)}
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto">
              <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4">
                <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                  {session.totalQuestions}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Questions</div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {session.correctAnswers}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Correct</div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4">
                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {formatTime(session.timeTaken)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Time</div>
              </div>
            </div>
          </div>
        </div>

        {/* Tier Advancement */}
        <div className="mb-6">
          {tierAdvanced && (
            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-3xl p-8 mb-6 text-center shadow-2xl">
              <div className="text-6xl mb-4 animate-bounce">🎊</div>
              <h2 className="text-3xl font-extrabold text-white mb-2">
                Tier Advancement!
              </h2>
              <p className="text-yellow-100 text-lg">
                You've advanced from {previousTier} to {session.progressTier}!
              </p>
            </div>
          )}

          <TierBadgeLarge
            tier={session.progressTier}
            animate={tierAdvanced}
            className="mb-6"
          />

          {/* Mastery Progress */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              Concept Mastery Progress
            </h3>
            <div className="relative">
              <div className="w-full h-8 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 transition-all duration-1000 ease-out flex items-center justify-end pr-3"
                  style={{ width: `${session.masteryPercentage || 0}%` }}
                >
                  <span className="text-white font-bold text-sm">
                    {Math.round(session.masteryPercentage || 0)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feedback Section */}
        {session.feedback && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg mb-6">
            <div className="flex items-start gap-4">
              <div className="text-4xl">💡</div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                  AI Feedback
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {session.feedback}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Strengths and Improvements */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Strengths */}
          {strengthAreas.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="text-3xl">💪</div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Strengths
                </h3>
              </div>
              <ul className="space-y-2">
                {strengthAreas.map((area, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-2 text-gray-700 dark:text-gray-300"
                  >
                    <svg
                      className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>{area}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Areas for Improvement */}
          {improvementAreas.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="text-3xl">🎯</div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Focus Areas
                </h3>
              </div>
              <ul className="space-y-2">
                {improvementAreas.map((area, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-2 text-gray-700 dark:text-gray-300"
                  >
                    <svg
                      className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>{area}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleContinueLearning}
            className="flex-1 py-4 px-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold text-lg rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            Practice Again
          </button>
          <button
            onClick={handleGoToDashboard}
            className="flex-1 py-4 px-6 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-bold text-lg rounded-xl transition-all duration-200 flex items-center justify-center gap-2"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Get performance message based on score percentage
 */
const getPerformanceMessage = (percentage) => {
  if (percentage >= 95) return "🌟 Outstanding! You've mastered this concept!";
  if (percentage >= 85) return "🎉 Excellent work! You're doing great!";
  if (percentage >= 70) return "👍 Good job! Keep up the great work!";
  if (percentage >= 50) return "📚 Not bad! A bit more practice will help!";
  return "💪 Keep practicing! You'll improve with time!";
};

/**
 * Format time in seconds to MM:SS
 */
const formatTime = (seconds) => {
  if (!seconds) return "0:00";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
};

export default ResultsPage;

